import { useCallback, useEffect, useState } from 'react';
import { fetchClients } from '@/api/clients';
import { useAuthGate } from '@/hooks/useAuthGate';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { AuthGate } from '@/components/AuthGate';
import { useColumnSort } from '@/lib/tableSort';
import { ClientsTable, type ClientSortKey } from './clients/ClientsTable';

/**
 * "Клиенты" — список кабинетов, доступных текущему администратору. Разметка
 * таблицы, строки и меню действий вынесены в pages/clients/*: страница держит
 * только состояние запроса (поиск, фильтр, сортировка, страница).
 */
export function ClientsPage() {
  const [page, setPage] = useState(1);
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [status, setStatus] = useState<string | null>(null);
  const sort = useColumnSort<ClientSortKey>();
  // Бампается после удаления клиента из меню строки, чтобы перезапросить список
  // (сам fetcher от него не зависит — просто триггер повторного useAuthGate).
  const [refreshKey, setRefreshKey] = useState(0);

  // Новый поиск/фильтр/сортировка – начинаем с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [search, status, sort.key, sort.dir]);

  const sortParam = sort.key ? (sort.dir === 'desc' ? `-${sort.key}` : sort.key) : undefined;

  const fetcher = useCallback(() => {
    const filter: Record<string, string> = {};
    // name, не company_name — "Компания" в таблице теперь строго Account.name
    // (см. докблок CustomerResource::company в Laravel), искать нужно по тому
    // же полю, что показывается, иначе поиск не находит то, что видно в таблице.
    if (search) filter.name = search;
    if (status) filter.status = status;
    return fetchClients({
      page,
      filter: Object.keys(filter).length ? filter : undefined,
      sort: sortParam,
    });
  }, [page, search, status, sortParam]);
  const state = useAuthGate(fetcher, [page, search, status, sortParam, refreshKey]);

  return (
    <AuthGate state={state}>
      {(data, isRefreshing) => (
        <ClientsTable
          data={data}
          isRefreshing={isRefreshing}
          page={page}
          onPageChange={setPage}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          status={status}
          onStatusChange={setStatus}
          sort={sort}
          onClientDeleted={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </AuthGate>
  );
}
